import React, { useEffect, useState } from 'react';
import { login, setToken, User, getPasskeyAuthOptions, verifyPasskeyAuth } from '../api';

interface Props {
  onLogin: (user: User) => void;
}

export default function Login({ onLogin }: Props) {
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [online, setOnline] = useState<boolean | null>(null);
  const [passkeyPending, setPasskeyPending] = useState(false);
  const [tempToken, setTempToken] = useState('');

  useEffect(() => {
    const check = () => {
      fetch('/api/health').then(r => setOnline(r.ok)).catch(() => setOnline(false));
    };
    check();
    const interval = setInterval(check, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(loginId, password);
      if (data.requirePasskey) {
        setTempToken(data.tempToken);
        setPasskeyPending(true);
        setLoading(false);
        // 자동으로 passkey 인증 시작
        handlePasskeyAuth(data.tempToken);
        return;
      }
      setToken(data.token);
      onLogin(data.user);
    } catch (err: any) {
      setError('로그인 실패. 아이디와 비밀번호를 확인하세요.');
    }
    setLoading(false);
  };

  const handlePasskeyAuth = async (token: string) => {
    setLoading(true);
    setError('');
    try {
      const { startAuthentication } = await import('@simplewebauthn/browser');
      const options = await getPasskeyAuthOptions(token);
      const credential = await startAuthentication({ optionsJSON: options });
      const result = await verifyPasskeyAuth(token, credential);
      if (result.token) {
        setToken(result.token);
        onLogin(result.user);
      } else {
        setError(result.error || '인증 실패');
      }
    } catch (e: any) {
      if (e.name === 'NotAllowedError') {
        setError('인증이 취소되었습니다.');
      } else {
        setError('Passkey 인증 실패: ' + (e.message || e));
      }
    }
    setLoading(false);
  };

  if (passkeyPending) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h1>
            <img src="/logo-linko.svg" alt="Linko" width="28" height="28" style={{ verticalAlign: 'middle', marginRight: 8 }} />
            Agent LINKO
          </h1>
          <p className="auth-desc">외부 접속이 감지되었습니다. Passkey 인증이 필요합니다.</p>
          {error && <div className="auth-error">{error}</div>}
          <button onClick={() => handlePasskeyAuth(tempToken)} disabled={loading} style={{ width: '100%', marginTop: 12 }}>
            {loading ? '인증 중...' : 'Passkey 인증'}
          </button>
          <button onClick={() => { setPasskeyPending(false); setTempToken(''); setError(''); }} style={{ width: '100%', marginTop: 8, background: 'none', border: '1px solid #333', color: '#888' }}>
            뒤로가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1 style={online === false ? { color: '#ff4444' } : undefined}>
          <img src="/logo-linko.svg" alt="Linko" width="28" height="28" style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Agent LINKO
        </h1>
        <p className="auth-desc">아이디와 비밀번호로 로그인하세요</p>
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="아이디"
            value={loginId}
            onChange={(e) => setLoginId(e.target.value)}
          />
          <input
            type="password"
            placeholder="비밀번호"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {error && <div className="auth-error">{error}</div>}
          <button type="submit" disabled={loading || !loginId || !password}>
            {loading ? '로그인 중...' : '로그인'}
          </button>
        </form>
      </div>
    </div>
  );
}
